import { useEffect, useState } from "react";

const TZ = "America/Anchorage";

/** Milliseconds until the next scheduled reload hour (Alaska time). */
export function msUntilNextReload(hours: number[] = [5, 13, 21], tz = TZ) {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const secondsNow = (get("hour") % 24) * 3600 + get("minute") * 60 + get("second");
  const targets = [...hours].sort((a, b) => a - b).map((x) => x * 3600);
  const next = targets.find((t) => t > secondsNow);
  const deltaSec = next != null ? next - secondsNow : 86400 - secondsNow + targets[0];
  return Math.max(0, deltaSec * 1000);
}

/** Live countdown (ms) to the next scheduled reload, ticking every second. */
export function useReloadCountdown(hours: number[] = [5, 13, 21], tz = TZ) {
  const [ms, setMs] = useState(() => msUntilNextReload(hours, tz));
  useEffect(() => {
    setMs(msUntilNextReload(hours, tz));
    const id = setInterval(() => setMs(msUntilNextReload(hours, tz)), 1000);
    return () => clearInterval(id);
  }, [hours.join(","), tz]);
  return ms;
}

export function formatCountdown(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export type ReloadStatus = {
  /** True when the last scheduled reload attempt could not be completed. */
  failed: boolean;
  /** Human-readable reason for the failure. */
  error: string | null;
  /** Timestamp (ms) of the last failed attempt. */
  failedAt: number | null;
  /** Number of consecutive failed attempts. */
  attempts: number;
};

/**
 * Hard-reload the page on a fixed daily schedule (Alaska time).
 * Default: 5am, 1pm, 9pm — i.e. every 8 hours starting at 5am.
 *
 * Before reloading, the page is pre-flighted. If the network/server is
 * unreachable the reload is skipped so the board keeps showing the last known
 * good data, an error state is returned for display, and the attempt is
 * retried with backoff until it succeeds.
 */
export function useScheduledReload(hours: number[] = [5, 13, 21], tz = TZ): ReloadStatus {
  const [status, setStatus] = useState<ReloadStatus>({
    failed: false,
    error: null,
    failedAt: null,
    attempts: 0,
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    let attempts = 0;

    const preflight = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("No network connection");
      }
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 10_000);
      try {
        const res = await fetch(`${window.location.pathname}?preflight=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
      } finally {
        clearTimeout(t);
      }
    };

    const attempt = async () => {
      try {
        await preflight();
        if (cancelled) return;
        window.location.reload();
      } catch (err) {
        if (cancelled) return;
        attempts += 1;
        setStatus({
          failed: true,
          error: err instanceof Error ? err.message : "Refresh failed",
          failedAt: Date.now(),
          attempts,
        });
        // Retry with backoff: 1, 2, 4… minutes, capped at 15 minutes.
        const delay = Math.min(15, 2 ** (attempts - 1)) * 60_000;
        timer = setTimeout(attempt, delay);
      }
    };

    const schedule = () => {
      timer = setTimeout(attempt, Math.max(1000, msUntilNextReload(hours, tz)));
    };

    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hours.join(","), tz]);

  return status;
}


/** Milliseconds until the next occurrence of the given minute-of-hour mark. */
export function msUntilMinuteMark(minute = 45, from: Date = new Date()) {
  const cur =
    from.getMinutes() * 60_000 + from.getSeconds() * 1000 + from.getMilliseconds();
  const target = (((minute % 60) + 60) % 60) * 60_000;
  const delta = target - cur;
  return delta > 0 ? delta : delta + 3_600_000;
}

/** Live countdown (ms) to the next hourly minute mark, ticking every second. */
export function useMinuteMarkCountdown(minute = 45) {
  const [ms, setMs] = useState(() => msUntilMinuteMark(minute));
  useEffect(() => {
    setMs(msUntilMinuteMark(minute));
    const id = setInterval(() => setMs(msUntilMinuteMark(minute)), 1000);
    return () => clearInterval(id);
  }, [minute]);
  return ms;
}

/**
 * Hard-reload the page once an hour, at the given minute mark (default :45).
 * Uses the same pre-flight + backoff behaviour as `useScheduledReload`, so a
 * failed refresh leaves the last known good data on screen.
 */
export function useHourlyReload(minute = 45): ReloadStatus {
  const [status, setStatus] = useState<ReloadStatus>({
    failed: false,
    error: null,
    failedAt: null,
    attempts: 0,
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    let attempts = 0;

    const preflight = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("No network connection");
      }
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 10_000);
      try {
        const res = await fetch(`${window.location.pathname}?preflight=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
      } finally {
        clearTimeout(t);
      }
    };

    const attempt = async () => {
      try {
        await preflight();
        if (cancelled) return;
        window.location.reload();
      } catch (err) {
        if (cancelled) return;
        attempts += 1;
        setStatus({
          failed: true,
          error: err instanceof Error ? err.message : "Refresh failed",
          failedAt: Date.now(),
          attempts,
        });
        const delay = Math.min(15, 2 ** (attempts - 1)) * 60_000;
        timer = setTimeout(attempt, delay);
      }
    };

    timer = setTimeout(attempt, Math.max(1000, msUntilMinuteMark(minute)));
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [minute]);

  return status;
}
